<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-8">
        <!-- Page Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 class="h3 mb-1">
              <i class="bi bi-person-plus me-2"></i>
              Nuevo Cliente
            </h1>
            <p class="text-muted mb-0">
              Registra un nuevo cliente en el sistema
            </p>
          </div>
          <router-link to="/clients" class="btn btn-outline-secondary">
            <i class="bi bi-arrow-left me-1"></i>
            Volver
          </router-link>
        </div>

        <!-- Form Card -->
        <div class="card shadow-sm">
          <div class="card-body">
            <form @submit.prevent="handleSubmit">
              <div class="row g-3">
                <div class="col-md-8">
                  <label for="nombre" class="form-label">
                    Nombre del Cliente <span class="text-danger">*</span>
                  </label>
                  <input
                    id="nombre"
                    v-model="formData.nombre"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.nombre }"
                    required
                    placeholder="Ej: Transporte San Martín S.A."
                  >
                  <div v-if="errors.nombre" class="invalid-feedback">
                    {{ errors.nombre }}
                  </div>
                </div>

                <div class="col-md-4">
                  <label for="cuit" class="form-label">
                    CUIT <span class="text-danger">*</span>
                  </label>
                  <input
                    id="cuit"
                    v-model="formData.cuit"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.cuit }"
                    required
                    placeholder="30-12345678-9"
                    maxlength="15"
                  >
                  <div v-if="errors.cuit" class="invalid-feedback">
                    {{ errors.cuit }}
                  </div>
                  <div class="form-text">
                    Formato: XX-XXXXXXXX-X
                  </div>
                </div>
              </div>

              <!-- Billing Information Section -->
              <div class="row g-3 mt-3">
                <div class="col-12">
                  <h5 class="h6 mb-3 text-secondary">
                    <i class="bi bi-credit-card me-2"></i>
                    Información de Facturación (Opcional)
                  </h5>
                </div>

                <div class="col-md-6">
                  <label for="fecha_inicio_facturacion" class="form-label">
                    Fecha de Inicio de Facturación
                  </label>
                  <input
                    id="fecha_inicio_facturacion"
                    v-model="formData.fecha_inicio_facturacion"
                    type="date"
                    class="form-control"
                  >
                  <div class="form-text">
                    Fecha desde la cual comienza a facturarse el servicio
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="cantidad_dias_corte" class="form-label">
                    Días hasta Corte de Servicio
                  </label>
                  <input
                    id="cantidad_dias_corte"
                    v-model.number="formData.cantidad_dias_corte"
                    type="number"
                    class="form-control"
                    min="0"
                    placeholder="Ej: 30"
                  >
                  <div class="form-text">
                    Cantidad de días antes de suspender el servicio por falta de pago
                  </div>
                </div>
              </div>

              <!-- Database Credentials Section -->
              <div class="row g-3 mt-3">
                <div class="col-12">
                  <h5 class="h6 mb-3 text-secondary">
                    <i class="bi bi-database me-2"></i>
                    Credenciales de Base de Datos (Opcional)
                  </h5>
                </div>

                <div class="col-md-4">
                  <label for="nombre_base_datos" class="form-label">
                    Nombre de Base de Datos
                  </label>
                  <input
                    id="nombre_base_datos"
                    v-model="formData.nombre_base_datos"
                    type="text"
                    class="form-control"
                    placeholder="Ej: mygeotab_db"
                  >
                </div>

                <div class="col-md-4">
                  <label for="usuario_base_datos" class="form-label">
                    Usuario de Base de Datos
                  </label>
                  <input
                    id="usuario_base_datos"
                    v-model="formData.usuario_base_datos"
                    type="text"
                    class="form-control"
                    placeholder="Ej: admin_user"
                    autocomplete="username"
                  >
                </div>

                <div class="col-md-4">
                  <label for="clave_base_datos" class="form-label">
                    Clave de Base de Datos
                  </label>
                  <input
                    id="clave_base_datos"
                    v-model="formData.clave_base_datos"
                    type="password"
                    class="form-control"
                    placeholder="••••••••"
                    autocomplete="new-password"
                  >
                </div>
              </div>

              <div class="row mt-4">
                <div class="col-12">
                  <div class="d-flex justify-content-end gap-2">
                    <router-link to="/clients" class="btn btn-outline-secondary">
                      Cancelar
                    </router-link>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      :disabled="loading"
                    >
                      <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                      <i v-else class="bi bi-check-lg me-1"></i>
                      Crear Cliente
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useClientStore } from '../../stores/clients'

export default {
  name: 'ClientCreate',
  setup() {
    const router = useRouter()
    const clientStore = useClientStore()

    const loading = ref(false)
    const formData = reactive({
      nombre: '',
      cuit: '',
      fecha_inicio_facturacion: null,
      cantidad_dias_corte: null,
      nombre_base_datos: '',
      usuario_base_datos: '',
      clave_base_datos: ''
    })
    const errors = reactive({})

    const validateForm = () => {
      const newErrors = {}

      if (!formData.nombre.trim()) {
        newErrors.nombre = 'El nombre es requerido'
      }

      if (!formData.cuit.trim()) {
        newErrors.cuit = 'El CUIT es requerido'
      } else {
        const cuitRegex = /^\d{2}-\d{8}-\d$/
        if (!cuitRegex.test(formData.cuit)) {
          newErrors.cuit = 'Formato de CUIT inválido (XX-XXXXXXXX-X)'
        }
      }

      Object.keys(errors).forEach(key => delete errors[key])
      Object.assign(errors, newErrors)

      return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
      if (!validateForm()) return

      loading.value = true
      try {
        await clientStore.createClient(formData)
        router.push('/clients')
      } catch (error) {
        // Error handled by store
      } finally {
        loading.value = false
      }
    }

    return {
      formData,
      errors,
      loading,
      handleSubmit
    }
  }
}
</script>
